import React, { useEffect, useState, useContext } from "react";
import Breadcrumb from "../../../CommonComponents/BreadCrumb/BreadCrumb";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import Button from "../../../Elements/Button";
import { networkService } from "../../../config/axios";
import DeleteOrgModal from "../../../Modals/Admin/DeleteOrgModal";
import UserInfoContext from "../../../../utils/userInfoContext";
import { faPen, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const AdminOrganizationDomainPage = () => {
    const { setShowNotifications, userInfoData } =
        useContext(UserInfoContext);

    const companyId = userInfoData?.company?.id;

    const [domains, setDomains] = useState([]);
    const [domainName, setDomainName] = useState("");
    const [isDefault, setIsDefault] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const [showRequestForm, setShowRequestForm] = useState(false);
    const [requestDomain, setRequestDomain] = useState("");

    useEffect(() => {
        if (companyId) fetchDomains();
    }, [companyId]);

    const fetchDomains = async () => {
        try {
            const res = await networkService(
                `/admin/organization_domains.json?company_id=${companyId}`,
                "GET"
            );
            setDomains(res?.data || []);
        } catch {
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: "Failed to load organization domains",
            });
        }
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                organization_domain: editingId
                    ? {
                        default: isDefault,
                        company_id: companyId,
                    }
                    : {
                        domain_name: domainName,
                        default: isDefault,
                        company_id: companyId,
                    },
            };

            if (editingId) {
                await networkService(
                    `/admin/organization_domains/${editingId}.json`,
                    "PUT",
                    payload
                );
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "Domain updated successfully",
                });
            } else {
                await networkService(
                    `/admin/organization_domains.json`,
                    "POST",
                    payload
                );
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "Domain added successfully",
                });
            }

            resetForm();
            fetchDomains();
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage:
                    error?.response?.data?.errors?.join(", ") ||
                    "Something went wrong",
            });
        }
    };

    const handleRequestDomain = async () => {
        try {
            await networkService(
                "/domain_requests.json",
                "POST",
                { domain: requestDomain }
            );

            setShowNotifications({
                showToast: true,
                toastType: "success",
                toastMessage: "Domain request sent to admin",
            });

            setRequestDomain("");
            setShowRequestForm(false);
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage:
                    error?.response?.data?.errors?.join(", ") ||
                    "Failed to send request",
            });
        }
    };

    const handleEdit = (domain) => {
        setEditingId(domain.id);
        setDomainName(domain.domain_name || domain.master_domain?.name);
        setIsDefault(domain.default);
        setShowForm(true);
    };

    const confirmDelete = async () => {
        try {
            await networkService(
                `/admin/organization_domains/${deleteId}.json`,
                "DELETE"
            );

            setShowNotifications({
                showToast: true,
                toastType: "success",
                toastMessage: "Domain deleted successfully",
            });

            setDeleteId(null);
            fetchDomains();
        } catch {
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: "Failed to delete domain",
            });
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setDomainName("");
        setIsDefault(false);
        setShowForm(false);
    };

    const columns = [
        {
            Header: "Domain",
            accessor: (row) => row.master_domain?.name || row.domain_name,
        },
        {
            Header: "Default",
            accessor: (row) => (row.default ? "Yes" : "No"),
        },
        {
            Header: "Actions",
            Cell: ({ row: { original } }) => (
                <div className="d-flex gap-2">
                    <FontAwesomeIcon
                        icon={faPen}
                        className="success-green cursor-pointer"
                        onClick={() => handleEdit(original)}
                    />
                    <FontAwesomeIcon
                        icon={faTrashCan}
                        className="cancel cursor-pointer"
                        onClick={() => setDeleteId(original.id)}
                    />
                </div>
            ),
        },
    ];

    return (
        <>
            <DeleteOrgModal
                title="Are you sure?"
                openModal={deleteId !== null}
                closeModal={() => setDeleteId(null)}
                name="delete this domain"
                handleDelete={confirmDelete}
            />

            <Breadcrumb string="Admin / Organization Domains" />

            <div className="container kinetizine">

                <div className="d-flex justify-content-end mb-3">
                    <Button
                        classList="success"
                        text="Request Domain"
                        onClick={() => setShowRequestForm(!showRequestForm)}
                    />
                </div>

                {showRequestForm && (
                    <div className="card p-3 mb-4">
                        <h5>Request New Domain</h5>

                        <input
                            className="form-control mb-3"
                            placeholder="Enter domain (example.com)"
                            value={requestDomain}
                            onChange={(e) => setRequestDomain(e.target.value)}
                        />

                        <div className="d-flex gap-2">
                            <Button
                                classList="success"
                                text="Send Request"
                                onClick={handleRequestDomain}
                                disabled={!requestDomain.trim()}
                            />
                            <Button
                                classList="secondary"
                                text="Cancel"
                                onClick={() => {
                                    setRequestDomain("");
                                    setShowRequestForm(false);
                                }}
                            />
                        </div>
                    </div>
                )}

                {showForm && (
                    <div className="card p-3 mb-4">
                        <h5>{editingId ? "Edit Domain" : "Add Domain"}</h5>

                        <input
                            className="form-control mb-3"
                            placeholder="Domain name (example.com)"
                            value={domainName}
                            disabled={!!editingId}
                            onChange={(e) => setDomainName(e.target.value)}
                        />

                        <label className="d-block mb-3">
                            <input
                                type="checkbox"
                                checked={isDefault}
                                onChange={() => setIsDefault(!isDefault)}
                            />{" "}
                            Default Domain
                        </label>

                        <div className="d-flex gap-2">
                            <Button
                                classList="success"
                                text={editingId ? "Update" : "Create"}
                                onClick={handleSubmit}
                                disabled={!editingId && !domainName.trim()}
                            />
                            <Button
                                classList="secondary"
                                text="Cancel"
                                onClick={resetForm}
                            />
                        </div>
                    </div>
                )}

                <CustomTable data={domains} columnData={columns} />
            </div>
        </>
    );
};

export default AdminOrganizationDomainPage;
